import React from 'react';
import { useDispatch } from 'react-redux';
import { Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { IconButton, Tooltip } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import WarningAmberIcon from '@material-ui/icons/Warning';
import styled from 'styled-components';
import { WhatsAppTemplateCatalogItem } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getWhatsAppTemplateCatalogAsync, syncWhatsAppTemplateCatalogAsync, deleteWhatsAppTemplateAsync } from '../../../ajax/requests';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { getErrorMessage } from '../../../ajax/errors';
import { SettingsItemContentProps } from '../SettingsItem';
import WhatsAppTemplateFormModal from './WhatsAppTemplateFormModal';
import AppButton from '../../../components/AppButton';

type StatusVariant = 'approved' | 'rejected' | 'pending' | 'disabled' | 'paused';

const isTemplateEditable = (status: string): boolean => {
    return status.toLowerCase() === 'rejected';
};

const getStatusTK = (status: string): TK => {
    switch (status.toLowerCase()) {
        case 'approved': return TK.whatsappTemplateStatusApproved;
        case 'rejected': return TK.whatsappTemplateStatusRejected;
        case 'pending': return TK.whatsappTemplateStatusPending;
        case 'disabled': return TK.whatsappTemplateStatusDisabled;
        case 'paused': return TK.whatsappTemplateStatusPaused;
        default: return TK.whatsappTemplateStatusPending;
    }
};

const getStatusVariant = (status: string): StatusVariant => {
    const s = status.toLowerCase();
    if (s === 'approved') return 'approved';
    if (s === 'rejected') return 'rejected';
    if (s === 'paused') return 'paused';
    if (s === 'disabled') return 'disabled';
    return 'pending';
};

const statusStyles: Record<StatusVariant, { background: string; color: string }> = {
    approved: { background: 'var(--success-bg)', color: 'var(--success-color)' },
    rejected: { background: 'rgba(220,53,69,0.1)', color: 'var(--danger-color)' },
    paused:   { background: 'var(--warning-bg)', color: '#856404' },
    disabled: { background: 'var(--bg-light)', color: 'var(--text-muted)' },
    pending:  { background: 'var(--bg-light)', color: 'var(--text-muted)' },
};

const Grid = styled.div`
    display: grid;
    gap: var(--spacer-S);
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const Card = styled.div<{ children?: React.ReactNode; $clickable?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
    padding: var(--spacer-S);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    background: white;
    cursor: ${p => p.$clickable ? 'pointer' : 'default'};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: ${p => p.$clickable ? 'var(--appBlue)' : 'var(--appLigthGrey)'};
        box-shadow: ${p => p.$clickable ? '0 2px 8px rgba(27, 110, 194, 0.1)' : 'none'};
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    gap: var(--spacer-XS);
    align-items: flex-start;
`;

const Badge = styled.span<{ variant: StatusVariant }>`
    flex-shrink: 0;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: ${p => statusStyles[p.variant].background};
    color: ${p => statusStyles[p.variant].color};
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
`;

const LoadingState = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
`;

const BodyPreview = styled.div`
    color: var(--text-muted);
    font-size: 0.82rem;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
    flex: 1;
`;

const CardActions = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    margin-top: auto;
    padding-top: var(--spacer-XS);
    border-top: var(--border-thin) solid var(--appLigthGrey);
`;

const META_POLICY_URL = 'https://business.whatsapp.com/policy?fbclid=IwY2xjawRa9jhleHRuA2FlbQIxMABicmlkETFYU3paRFdvZVZVWWFSZTc4c3J0YwZhcHBfaWQPNTE0NzcxNTY5MjI4MDYxAAEecbYDg0LsEeEZQf1IIsDlz7SxXKWDeI4tLXIlxcxkEqIb_YIOo5ge0R8Za0E_aem_ptzDL17JEtdwrqK2n2tHAQ';

const RejectedPolicyContent: React.FC = () => (
    <span style={{ display: 'block', maxWidth: 260, lineHeight: 1.5 }}>
        Este modelo foi rejeitado porque não cumpre uma ou mais das políticas da Meta.{' '}
        Podes pedir uma revisão na{' '}
        <a
            href={META_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#90caf9', textDecoration: 'underline' }}
            onClick={e => e.stopPropagation()}
        >
            Página de apoio para negócios
        </a>
        {' '}ou editar este modelo e voltar a enviar.
    </span>
);

const AddTemplateButton = styled(IconButton)`
    && {
        padding: 0.35rem;
        color: var(--text-muted);
        border: var(--border-thin) dashed var(--appLigthGrey);
        border-radius: var(--spacer-XS);
        width: 100%;
        transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

        &:hover {
            border-color: var(--success-color);
            color: var(--success-color);
            background: var(--success-bg);
        }
    }
`;

const WhatsAppTemplatesSettings: React.FC<SettingsItemContentProps> = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const [templates, setTemplates] = React.useState<WhatsAppTemplateCatalogItem[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [syncing, setSyncing] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);
    const [editingTemplate, setEditingTemplate] = React.useState<WhatsAppTemplateCatalogItem | undefined>(undefined);
    const [deletingTemplate, setDeletingTemplate] = React.useState<WhatsAppTemplateCatalogItem | undefined>(undefined);
    const [deleting, setDeleting] = React.useState(false);
    const [bannerDismissed, setBannerDismissed] = React.useState(false);

    const loadTemplates = React.useCallback(async () => {
        setLoading(true);
        try {
            setTemplates(await getWhatsAppTemplateCatalogAsync());
        } catch (error) {
            console.error(error);
            dispatch(alertError(getErrorMessage(error) || t(TK.somethingWentWrong)));
        } finally {
            setLoading(false);
        }
    }, [dispatch, t]);

    React.useEffect(() => {
        void loadTemplates();
    }, [loadTemplates]);

    const handleSync = React.useCallback(async () => {
        setSyncing(true);
        try {
            await syncWhatsAppTemplateCatalogAsync();
            await loadTemplates();
        } catch (error) {
            console.error(error);
            dispatch(alertError(getErrorMessage(error) || t(TK.somethingWentWrong)));
        } finally {
            setSyncing(false);
        }
    }, [dispatch, loadTemplates, t]);

    const handleOpenCreate = React.useCallback(() => {
        setEditingTemplate(undefined);
        setFormOpen(true);
    }, []);

    const handleOpenEdit = React.useCallback((template: WhatsAppTemplateCatalogItem) => {
        setEditingTemplate(template);
        setFormOpen(true);
    }, []);

    const handleCloseForm = React.useCallback(() => {
        setFormOpen(false);
    }, []);

    const handleFormSuccess = React.useCallback(() => {
        void loadTemplates();
    }, [loadTemplates]);

    const handleDeleteConfirm = React.useCallback(async () => {
        if (!deletingTemplate) return;
        setDeleting(true);
        try {
            await deleteWhatsAppTemplateAsync(deletingTemplate.providerTemplateId, deletingTemplate.providerTemplateName);
            dispatch(alertSuccess(t(TK.whatsappTemplateDeleteSuccess)));
            setDeletingTemplate(undefined);
            void loadTemplates();
        } catch (error) {
            console.error(error);
            dispatch(alertError(getErrorMessage(error) || t(TK.somethingWentWrong)));
        } finally {
            setDeleting(false);
        }
    }, [deletingTemplate, dispatch, t, loadTemplates]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacer-S)', flexWrap: 'wrap' }}>
                <div style={{ color: 'var(--text-muted)' }}>{t(TK.whatsappTemplatesDescription)}</div>
                <div style={{ display: 'flex', gap: 'var(--spacer-XS)', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Tooltip title={t(TK.whatsappTemplatesSync)}>
                        <span>
                            <IconButton size="small" disabled={syncing} onClick={handleSync}>
                                {syncing
                                    ? <Spinner size="sm" style={{ width: '1rem', height: '1rem' }} />
                                    : <RefreshIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <AppButton color="success" onClick={handleOpenCreate}>
                        {t(TK.whatsappTemplatesCreate)}
                    </AppButton>
                </div>
            </div>

            {!bannerDismissed && (
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--spacer-XS)',
                    padding: 'var(--spacer-S)',
                    background: 'var(--warning-bg)',
                    border: 'var(--border-thin) solid var(--warning-border)',
                    borderRadius: 'var(--spacer-XS)',
                    color: 'var(--text-body)',
                    fontSize: '0.9rem',
                }}>
                    <span style={{ flex: 1 }}>{t(TK.whatsappTemplateMetaApprovalDisclaimer)}</span>
                    <IconButton size="small" onClick={() => setBannerDismissed(true)} style={{ padding: '0.1rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
            )}

            {loading ? (
                <LoadingState>
                    <Spinner size="sm" />
                    <span>{t(TK.loading)}</span>
                </LoadingState>
            ) : templates.length === 0 ? (
                <div style={{ padding: 'var(--spacer-S)', border: 'var(--border-thin) dashed var(--appLigthGrey)', borderRadius: 'var(--spacer-XS)' }}>
                    {t(TK.whatsappTemplatesNoCampaignTemplates)}
                </div>
            ) : (
                <Grid>
                    {templates.map(template => {
                        const bodyComponent = template.componentsSnapshot.find(c => c.type.toUpperCase() === 'BODY');
                        const variant = getStatusVariant(template.status);
                        const infoLines = [
                            `${t(TK.language)}: ${template.language}`,
                            `${t(TK.category)}: ${template.category}`,
                            `${t(TK.version)}: ${template.version}`,
                            ...(template.allowedUseCases.length > 0 ? [template.allowedUseCases.join(', ')] : []),
                        ].join('\n');
                        const isRejected = variant === 'rejected';
                        const infoTooltipContent = isRejected
                            ? <span><span style={{ whiteSpace: 'pre-line', display: 'block', marginBottom: '0.4rem' }}>{infoLines}</span><RejectedPolicyContent /></span>
                            : <span style={{ whiteSpace: 'pre-line' }}>{infoLines}</span>;
                        const editable = isTemplateEditable(template.status);
                        return (
                            <Card key={template.id} $clickable={editable} onClick={() => { if (editable) handleOpenEdit(template); }}>
                                <Header>
                                    <div style={{ fontWeight: 800 }}>{template.displayName}</div>
                                    {isRejected ? (
                                        <Tooltip interactive title={<RejectedPolicyContent />} placement="top">
                                            <Badge variant={variant} onClick={e => e.stopPropagation()}>{t(getStatusTK(template.status))}</Badge>
                                        </Tooltip>
                                    ) : (
                                        <Badge variant={variant}>{t(getStatusTK(template.status))}</Badge>
                                    )}
                                </Header>
                                {bodyComponent?.text && (
                                    <BodyPreview>{bodyComponent.text}</BodyPreview>
                                )}
                                {template.rejectedReason ? (
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacer-XXS)', color: 'var(--danger-color)', fontSize: '0.82rem' }}>
                                        <WarningAmberIcon style={{ fontSize: '1rem', flexShrink: 0, marginTop: '0.05rem' }} />
                                        <span>{template.rejectedReason}</span>
                                    </div>
                                ) : null}
                                <CardActions>
                                    <Tooltip interactive title={infoTooltipContent} placement="top">
                                        <IconButton size="small" onClick={e => e.stopPropagation()}>
                                            <InfoOutlinedIcon fontSize="small" style={{ color: isRejected ? 'var(--danger-color)' : 'var(--text-muted)' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--spacer-XS)' }}>
                                        <Tooltip title={editable ? t(TK.edit) : variant === 'approved' ? t(TK.whatsappTemplateApprovedCannotEdit) : t(TK.whatsappTemplateCannotEdit)} placement="top">
                                            <span>
                                                <IconButton size="small" disabled={!editable} onClick={e => { e.stopPropagation(); if (editable) handleOpenEdit(template); }}>
                                                    <EditIcon fontSize="small" style={{ color: editable ? 'var(--text-muted)' : 'var(--appLigthGrey)' }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={t(TK.delete)} placement="top">
                                            <IconButton size="small" onClick={e => { e.stopPropagation(); setDeletingTemplate(template); }}>
                                                <DeleteIcon fontSize="small" style={{ color: 'var(--danger-color)' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </CardActions>
                            </Card>
                        );
                    })}
                </Grid>
            )}

            {!loading && (
                <Tooltip title={t(TK.whatsappTemplatesCreate)} placement="top">
                    <AddTemplateButton onClick={handleOpenCreate}>
                        <AddIcon />
                    </AddTemplateButton>
                </Tooltip>
            )}

            <WhatsAppTemplateFormModal
                isOpen={formOpen}
                template={editingTemplate}
                onClose={handleCloseForm}
                onSuccess={handleFormSuccess}
            />

            <Modal isOpen={!!deletingTemplate} toggle={() => setDeletingTemplate(undefined)}>
                <ModalHeader toggle={() => setDeletingTemplate(undefined)}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>
                    <p>{t(TK.whatsappTemplateDeleteConfirm)}</p>
                    {deletingTemplate && (
                        <p style={{ fontWeight: 800 }}>{deletingTemplate.displayName}</p>
                    )}
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={() => setDeletingTemplate(undefined)} disabled={deleting}>
                        {t(TK.cancel)}
                    </AppButton>
                    <AppButton color="danger" onClick={handleDeleteConfirm} disabled={deleting}>
                        {deleting ? <Spinner size="sm" /> : t(TK.delete)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default WhatsAppTemplatesSettings;
